import { useEffect } from "react";
import { fetchCertificationById } from "api/certifications/certifications";
import Swal from "sweetalert2";
import { handleApiError } from "utils";

/**
 * Hook que maneja el ciclo de vida de la certificación, incluyendo:
 * - Redirección si no existe la certificación.
 * - Validación del estado de la certificación desde la API.
 * - Muestra una alerta si la certificación no está disponible.
 *
 * Este hook asegura que el usuario no continúe con una certificación inválida o ya completada.
 *
 * @param {Object} params - Parámetros necesarios para controlar el ciclo de vida.
 * @param {Object} params.certification - Objeto de certificación con al menos un `id` y `status`.
 * @param {string} params.token - Token de autenticación para realizar llamadas a la API.
 * @param {Function} params.dispatch - Función dispatch de Redux (no usada actualmente, pero disponible para futuras extensiones).
 * @param {Function} params.navigate - Función de navegación de React Router.
 */
const useCertificationLifecycle = ({ certification, token, dispatch, navigate }) => {
	useEffect(() => {
		if (!certification) navigate('/');
	}, [certification, navigate]);

	useEffect(() => {
		if (token) loadCertificationById();
	}, [token]);

	/**
	 * Carga la certificación por ID y verifica si ya no está disponible (estado > 1).
	 * Si no está disponible, muestra una alerta y redirige al inicio.
	 */
	const loadCertificationById = async () => {
		try {
			const { data } = await fetchCertificationById(certification.id, token);
			if (data?.status === 200 && data.message.status > 1) {
				certificationNotAvaible();
			}
		} catch (error) {
			handleApiError(error);
		}
	};

	/**
	 * Muestra un mensaje de error indicando que la certificación no está disponible
	 * y redirige al usuario a la página principal.
	 */
	const certificationNotAvaible = () => {
		Swal.fire({
			title: 'Error',
			text: 'Esta certificación no está disponible.',
			icon: 'error',
			allowOutsideClick: false,
		}).then(() => navigate('/'));
	};
};

export default useCertificationLifecycle;